import React, { useState, useContext, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { BookingContext } from "../../../context/BookingContext";
import { Types } from "../../../context/search.reducer";
import { Types as RequestTypes } from "../../../context/request.reducer";
import { Types as SavedRequestTypes } from "../../../context/savedRequest.reducer";
import {
  Classroom,
  ClassroomFilterFieldsInput,
  ClassroomOrderByFields,
  DateAvailabilityFilterInput,
} from "@models/ISchema";
import CardBooking from "../../../components/CardBooking/CardBooking";
import useClassroomsAvailabilities from "@modules/booking/hooks/useClassroomsAvailabilities";
import { ContextEDH } from "@context/ContextEDH";
import { BookingClassroomOrderPayload } from "@context/base.reducer";
import { Header } from "@common/components";
import {
  Button,
  CardNotification,
  Divider,
  Heading,
  Link,
  Loading,
  RoundButton,
  Tag,
  Text,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import ClassroomParamsItem from "@modules/booking/components/classroom-params-item";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import css from "./results.module.scss";
import { Icon } from "@foris/avocado-icons";
import CoverageResumeDrawer from "@modules/booking/components/coverage-resume-drawer";
import useClassroomCoverage from "@modules/booking/hooks/useClassroomCoverage";
import { useHistory } from "react-router-dom";
import { getDateNumber } from "@utils/formats";
import { getEditedClassroomAssignedDates } from "@modules/booking/utils/classroomEvents";
import useClassroomBookingsMutation from "@modules/booking/hooks/useClassroomBookingsMutation";
import ClassroomClashesDrawer from "@modules/booking/components/classroom-clashes-drawer";

const getInitialOrderByValue = (ordersBySetting: BookingClassroomOrderPayload = []) => {
  if (!ordersBySetting?.length) {
    return [
      {
        field: ClassroomOrderByFields.Name,
        direction: "ASC",
      },
    ];
  }

  return [ordersBySetting?.[0]];
};

const ClassroomResults = () => {
  const history = useHistory();
  const { state: edhContext } = useContext(ContextEDH);
  const { dispatch, state } = useContext(BookingContext);
  const { scenario, origin, isAdminBooking, contextUrl } = useBookingContextUrl();
  const [{ error, loading }, results, getClassroomsAvailabilites] = useClassroomsAvailabilities(
    origin,
    scenario,
    state?.request?.editedBooking?.id,
  );
  const [showSelectionResume, setShowSelectionResume] = useState(
    state?.search?.selectedClassrooms?.length > 0,
  );
  const [showSelectionResumeDrawer, setShowSelectionResumeDrawer] = useState(false);
  const { preferences, requirements } = state?.search;
  const [pagination, setPagination] = useState({
    page: 1,
    size: 5,
  });
  const [orderByValue, setOrderByValue] = useState(
    getInitialOrderByValue(edhContext.base.bookingClassroomOrder),
  );
  const { t } = useTranslation();
  const { adaptedHomeUrl } = useNavigationUrl();
  const [showClassroomClashesDrawer, setShowClassroomClashesDrawer] = useState(false);

  const [mutationResult, submitClassroomBookingsMutation] = useClassroomBookingsMutation({
    origin,
    scenario,
    onSuccess: booking => {
      dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });
      dispatch({ type: RequestTypes.ClearRequest, payload: null });
      dispatch({ type: Types.SetView, payload: "search" });

      dispatch({ type: Types.SetCurrentBooking, payload: booking });
      dispatch({ type: Types.ClearSearch, payload: null });
      dispatch({
        type: RequestTypes.SetSnackbar,
        payload: { display: true, message: "Sala actualizada exitosamente", error: false },
      });
      dispatch({
        type: RequestTypes.SetUserResponsible,
        payload: { value: booking?.responsible?.id, error: false },
      });
      dispatch({
        type: RequestTypes.SetEventType,
        payload: { value: booking?.type?.id, error: false },
      });

      // redirect to the created booking (remember that booking updates
      // deletes the edited booking and creates a new one)
      history.replace(`/booking/detail/${contextUrl}/${booking?.id}`);
    },
  });

  const { adaptedSelectedDates, totalAndCoveredDates } = useClassroomCoverage({
    dateTimeFormatted: state?.search?.dateTimeFormatted,
    selectedClassrooms: state?.search?.selectedClassrooms,
  });

  const handleRemoveClassroom = (classroom: Classroom) => {
    const filteredSelectedClassrooms = state?.search?.selectedClassrooms?.filter(
      selectedClassroom => selectedClassroom.classroom.id !== classroom.id,
    );

    dispatch({
      type: Types.SetSelectedClassrooms,
      payload: filteredSelectedClassrooms,
    });

    if (filteredSelectedClassrooms?.length === 0) {
      setShowSelectionResume(false);
    }
  };

  const calculateSessions = () => {
    // Sometimes the `state.search.dateTimes` `value` are an actual date
    // instead of a number (or raw dayId). We have to consider it to build the
    // recurrent sessions correctly.
    const getDayId = (day: any) => {
      const isNumeric = (value: string) => /^-?\d+$/.test(value);
      if (isNumeric(day?.value)) return day?.value;
      const dayId = dayjs(day?.value).day();
      return dayId === 0 ? "7" : dayId.toString();
    };

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = ("0" + (date.getMonth() + 1)).slice(-2);
      const day = ("0" + date.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    };

    const isSameDay = (date: Date, dayId: string) => {
      const dateDayId = date.getDay().toString();
      return dateDayId === "0" ? dayId === "7" : dateDayId === dayId;
    };

    const selectedWeeks = (state?.search?.intervals ?? []).filter(week => week?.selected ?? false);
    const selectedDates: DateAvailabilityFilterInput[] = [];

    // recurrent booking
    if (Boolean(selectedWeeks?.length)) {
      selectedWeeks?.forEach(week => {
        const weekStart = new Date(week?.startingDate);
        const weekEnd = new Date(week?.endingDate);

        state?.search?.dateTime?.forEach(date => {
          const dayId = getDayId(date?.day);
          const currDate = new Date(weekStart.getTime());

          // move forward one day at a time until we reach the target day
          while (!isSameDay(currDate, dayId)) {
            currDate.setDate(currDate.getDate() + 1);
          }

          // keep adding one session per week for the defined day/start/end
          // until the `end` date is reached
          while (currDate <= weekEnd) {
            const [startHour, startMinute, startSecond] = date?.start?.value?.value?.split(":");
            const [endHour, endMinute, endSecond] = date?.end?.value?.value?.split(":");

            const start = `${formatDate(currDate)} ${getDateNumber(startHour)}:${getDateNumber(
              startMinute || 0,
            )}:${getDateNumber(startSecond || 0)}`;
            const end = `${formatDate(currDate)} ${getDateNumber(endHour || 0)}:${getDateNumber(
              endMinute || 0,
            )}:${getDateNumber(endSecond || 0)}`;

            const dateKey = `${start} - ${end.split(" ")[1]}`;
            const isMissingDateTime = totalAndCoveredDates?.missingDatesMap?.has(dateKey);

            if (isMissingDateTime) {
              selectedDates.push({ start, end });
            }

            // move a week forward
            currDate?.setDate(currDate?.getDate() + 7);
          }
        });
      });
    }
    // uniq sessions
    else {
      state?.search?.dateTime?.forEach(date => {
        const start = `${date?.day?.value} ${date?.start?.value?.value}`;
        const end = `${date?.day?.value} ${date?.end?.value?.value}`;

        selectedDates.push({ start, end });
      });
    }

    return selectedDates;
  };

  const selectedDates: DateAvailabilityFilterInput[] = useMemo(calculateSessions, [
    state?.search?.intervals,
    state?.search?.dateTime,
    totalAndCoveredDates?.missingDatesMap,
  ]);

  const datesInSelectedClassrooms = useMemo(() => {
    return state?.search?.selectedClassrooms?.reduce(
      (acc, selection) => {
        selection?.dates?.forEach(({ day, start, end }) => {
          const startDate = `${day?.dateValue} ${start?.value?.value}`;
          const endDate = `${day?.dateValue} ${end?.value?.value}`;

          acc["start"][startDate] = true;
          acc["end"][endDate] = true;
        });

        return acc;
      },
      { start: {}, end: {} },
    );
  }, [state?.search?.selectedClassrooms]);

  const fields: ClassroomFilterFieldsInput[] = useMemo(() => {
    const filters: ClassroomFilterFieldsInput[] = [];

    if (!isAdminBooking) {
      filters.push({ usableInBookings: { is: true } });
    }

    if (preferences?.building) {
      filters.push({ buildingId: { eq: parseInt(preferences.building.value) } });
    }

    if (preferences?.classroomType) {
      filters.push({ classroomTypeId: { eq: parseInt(preferences.classroomType.value) } });
    }

    if (requirements?.people) {
      filters.push({ capacity: { value: { gte: requirements.people.count } } });
    }

    const datesNotInSelectedClassrooms = selectedDates?.filter(
      date =>
        !datesInSelectedClassrooms?.["start"]?.[date?.start] &&
        !datesInSelectedClassrooms?.["end"]?.[date?.end],
    );

    const datesToFilter = datesNotInSelectedClassrooms?.length
      ? datesNotInSelectedClassrooms
      : selectedDates;

    if (datesToFilter?.length) {
      filters.push({ dateAvailability: datesToFilter });
    }

    return filters;
  }, [preferences, requirements, selectedDates, orderByValue, datesInSelectedClassrooms]);

  useEffect(() => {
    if (!fields || showSelectionResume) return;
    getClassroomsAvailabilites(
      pagination?.page,
      pagination?.size,
      fields,
      orderByValue ?? undefined,
    );
  }, [pagination, fields, showSelectionResume]);

  const handleContinueBooking = () => {
    const params = !isAdminBooking ? `?scenario=${scenario}&origin=${origin}` : "";
    const requestPath = `/booking/${
      !isAdminBooking ? "create/" : ""
    }request/${contextUrl}${params}`;

    if (!!state?.search?.currentBooking) {
      const payload = getEditedClassroomAssignedDates(state?.search?.selectedClassrooms);
      submitClassroomBookingsMutation(
        {
          ...state?.search?.currentBooking,
          classroomAssignedDates: payload,
        },
        false,
        false,
      );
      return;
    }

    history.push(requestPath);
  };

  if (error) {
    console.error(error);
    return null;
  }

  return (
    <section className={css.classroomResults}>
      {mutationResult?.isLoading && <Loading />}

      <Header
        breadcrumbItems={[
          { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
          { title: t("booking.title") },
        ]}
        title={t("booking.title")}
      />

      <div className={css.classroomResults_content}>
        <div className={css.contentHeader}>
          <RoundButton
            size="md"
            variant="ghost"
            icon="chevron-left"
            onClick={() => {
              if (showSelectionResume) {
                setShowSelectionResume(false);
              } else {
                dispatch({ type: Types.SetView, payload: "search" });
              }
            }}
          />

          <Heading type="h2">
            {showSelectionResume
              ? "Cobertura de la selección"
              : t("booking.classroom-search.title")}
          </Heading>
        </div>

        <div className={css.contentParams}>
          <div className={css.contentParams_details}>
            <Heading type="h4">{t("booking.booking-request.resume.search-params")}</Heading>

            <div className={css.contentParams_items}>
              <ClassroomParamsItem
                title={t("booking.booking-request.resume.event-type")}
                value={
                  state?.search?.recurrence
                    ? t("booking.booking-request.resume.recurrent-event")
                    : t("booking.booking-request.resume.single-event")
                }
              />
              <ClassroomParamsItem
                title={t("booking.booking-request.resume.capacity-required")}
                value={state?.search?.requirements?.people?.count}
              />
              {state?.search?.preferences?.building?.name && (
                <ClassroomParamsItem
                  title={t("booking.booking-request.resume.building")}
                  value={state?.search?.preferences?.building?.name}
                />
              )}
              {state?.search?.preferences?.classroomType?.name && (
                <ClassroomParamsItem
                  title={t("booking.booking-request.resume.classroom-type")}
                  value={state?.search?.preferences?.classroomType?.name}
                />
              )}
            </div>

            <ClassroomParamsItem
              title={t("booking.booking-request.resume.time-range")}
              value={adaptedSelectedDates}
            />
          </div>
        </div>

        {!!state?.search?.selectedClassrooms?.length && showSelectionResume && (
          <>
            <section className={css.classroomsResume}>
              <div className={css.classroomsResume_selection}>
                <div className={css.selectionsHeader}>
                  <Heading type="h3">
                    {t("booking.booking-request.selection-resume.selected-classrooms")}
                  </Heading>

                  <Link
                    className={css.selectionsHeader_link}
                    size="sm"
                    onClick={() => setShowSelectionResumeDrawer(true)}
                  >
                    {t("booking.booking-request.selection-resume.check-selection-coverage")}
                    <Icon name="chevron-right" size="sm" />
                  </Link>
                </div>

                <div className={css.selectionsContent}>
                  {state?.search?.selectedClassrooms?.map(({ classroom }) => (
                    <Tag
                      icon="close"
                      key={classroom.id}
                      onIconClick={() => handleRemoveClassroom(classroom)}
                    >
                      {classroom.name}
                    </Tag>
                  ))}
                </div>
              </div>

              <Divider className={css.classroomsResume_divider} />

              <div className={css.classroomsResume_coverage}>
                <div className={css.coverageHeader}>
                  <Heading type="h3">
                    {t("booking.booking-request.selection-resume.classrooms-coverage")}
                  </Heading>

                  {!!totalAndCoveredDates?.missing?.length && (
                    <Link
                      className={css.coverageHeader_link}
                      iconRight="chevrons-right"
                      size="sm"
                      onClick={() => setShowClassroomClashesDrawer(true)}
                    >
                      {t("booking.booking-request.selection-resume.check-clashes")}
                    </Link>
                  )}
                </div>

                {!!totalAndCoveredDates?.covered?.length && (
                  <div className={css.coverageContent}>
                    <Text className={css.coverageContent_title} weight="medium">
                      <Icon name="circle-check" color="success-50" size="sm" filled />{" "}
                      {t("booking.booking-request.selection-resume.covered-dates", {
                        count: totalAndCoveredDates?.covered?.length,
                        total: totalAndCoveredDates?.total?.length,
                      })}
                    </Text>

                    <div className={css.coverageContent_details}>
                      {totalAndCoveredDates?.covered?.map((date, key) => (
                        <>
                          <span className={css.detailsItem} key={key}>
                            {date?.day?.dateValue} {date?.start?.value?.label} -{" "}
                            {date?.end?.value?.label}
                          </span>
                          {key !== totalAndCoveredDates?.covered?.length - 1 && (
                            <span
                              className={css.detailsSeparator}
                              key={`separator-covered-${key}`}
                            />
                          )}
                        </>
                      ))}
                    </div>
                  </div>
                )}

                {!!totalAndCoveredDates?.missing?.length && (
                  <div className={css.coverageContent}>
                    <Text className={css.coverageContent_title} weight="medium">
                      <Icon name="alert-triangle" color="warning-50" size="sm" filled />{" "}
                      {t("booking.booking-request.selection-resume.missing-dates", {
                        count: totalAndCoveredDates?.missing?.length,
                        total: totalAndCoveredDates?.total?.length,
                      })}
                    </Text>

                    <div className={css.coverageContent_details}>
                      {totalAndCoveredDates?.missing?.map((date, key) => (
                        <>
                          <span className={css.detailsItem} key={key}>
                            {date?.day?.dateValue} {date?.start?.value?.label} -{" "}
                            {date?.end?.value?.label}
                          </span>
                          {key !== totalAndCoveredDates?.missing?.length - 1 && (
                            <span
                              className={css.detailsSeparator}
                              key={`separator-missing-${key}`}
                            />
                          )}
                        </>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            <div className={css.classroomResults_resumeActions}>
              <Button
                variant="ghost"
                onClick={() => {
                  dispatch({ type: RequestTypes.ClearRequest, payload: null });
                  dispatch({ type: Types.ClearSearch, payload: null });
                  history.replace(`/booking/list/${contextUrl}`);
                }}
              >
                {t("booking.booking-request.actions.cancel")}
              </Button>

              {!!totalAndCoveredDates?.covered?.length && (
                <Button variant="secondary" onClick={handleContinueBooking}>
                  {totalAndCoveredDates?.covered?.length === totalAndCoveredDates?.total?.length
                    ? t("booking.booking-request.actions.continue-booking")
                    : `${t("booking.booking-request.actions.book-with-selected-dates")} (
                  ${totalAndCoveredDates?.covered?.length}/${totalAndCoveredDates?.total?.length})`}
                </Button>
              )}

              {!!totalAndCoveredDates?.missing?.length && (
                <Button
                  onClick={() => {
                    setPagination({ page: 1, size: 5 });
                    setShowSelectionResume(false);
                  }}
                >
                  {t("booking.booking-request.actions.search-for-missing-dates")}
                </Button>
              )}
            </div>
          </>
        )}

        {(!!results?.items?.length || loading) && !showSelectionResume && (
          <CardBooking
            items={results?.items}
            isLoadingItems={loading}
            orderBy={orderByValue}
            pageInfo={results?.pageInfo}
            pageSize={pagination?.size}
            onSelectClassroom={() => setShowSelectionResume(true)}
            onChangePage={page => {
              setPagination(state => ({ page, size: state.size }));
            }}
            onChangePageSize={size => {
              setPagination({ page: 1, size });
            }}
            onChangeOrderBy={setOrderByValue}
          />
        )}

        {!results?.items?.length && !loading && !showSelectionResume && (
          <CardNotification state="info" title={t("empty-results.title")} outlined>
            {t("empty-results.description-classrooms")}
          </CardNotification>
        )}
      </div>

      <CoverageResumeDrawer
        isOpen={showSelectionResumeDrawer}
        onClose={() => setShowSelectionResumeDrawer(false)}
      />

      <ClassroomClashesDrawer
        isOpen={showClassroomClashesDrawer}
        missingDates={totalAndCoveredDates?.missing}
        onClose={() => setShowClassroomClashesDrawer(false)}
      />
    </section>
  );
};

export default ClassroomResults;
